import { observer } from 'mobx-react-lite';
import React, { useContext } from 'react'
import { InfluencerContext } from '../../../../contexts/InfluencerContext';
import { CurrentProjectManagerContext } from '../../../../contexts/CurrentProjectManagerContext';
import RatingBox from './RatingsParts/RatingBox';
import RatingButton from './RatingsParts/RatingButton';
import CustomRating from '../../../FormComponents/CustomRating';
import { RATING_LABELS } from './RatingsParts/StarPicker';
import { format, isValid } from 'date-fns'
import { Person, StarFill } from 'react-bootstrap-icons';

const formatDate = value => {
  const date = new Date(value)
  return value && isValid(date) ? format(date, 'yyyy.MM.dd') : null
}

const ScoreRow = ({ title, score, description }) => {
  const rounded = Math.round(+score)

  return (
    <div className="flex-1 min-w-0">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{title}</p>
      <div className="flex items-center gap-2 mt-1">
        <CustomRating initialRating={+score} readonly />
        <span className="text-sm font-bold dark:text-white">{+score % 1 ? (+score).toFixed(1) : rounded}/5</span>
        {!(+score % 1) && <span className="text-xs text-gray-500 dark:text-gray-400">{RATING_LABELS[rounded]}</span>}
      </div>
      {description
        ? <p className="mt-2 text-sm whitespace-pre-line break-words dark:text-white">{description}</p>
        : <p className="mt-2 text-sm italic text-gray-400">No comment</p>
      }
    </div>
  )
}

export default observer(function Ratings() {
  const { ratings, professionalRating, qualityRating, combinedRating } = useContext(InfluencerContext)
  const { currentProjectManager } = useContext(CurrentProjectManagerContext)

  const isOwn = rating => +rating.project_manager_id === +currentProjectManager?.id
  const hasOwnRating = ratings.some(isOwn)

  return (
    <div className="flex flex-col gap-6 max-w-5xl">
      <div className="w-full bg-white dark:bg-rb-dark-mode p-6 rounded-xl">
        <div className="flex justify-between items-center mb-5">
          <p className="font-bold text-xl dark:text-white">Review summary</p>
          <RatingButton hasOwnRating={hasOwnRating} />
        </div>
        <div className="flex gap-4">
          <RatingBox text="Combined" rating={combinedRating} number_of_reviews={ratings.length} highlighted />
          <RatingBox text="Professional work" rating={professionalRating} number_of_reviews={ratings.length} />
          <RatingBox text="Quality of content" rating={qualityRating} number_of_reviews={ratings.length} />
        </div>
      </div>

      <div>
        <p className="font-bold text-xl mb-4 dark:text-white">Reviews {ratings.length > 0 && <span className="text-gray-400 font-semibold">({ratings.length})</span>}</p>

        {ratings.length === 0 && (
          <div className="bg-white dark:bg-rb-dark-mode rounded-xl p-10 flex flex-col items-center text-center">
            <StarFill className="w-8 h-8 text-yellow-400 mb-3" />
            <p className="font-bold dark:text-white">No reviews yet</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Be the first to rate this influencer's professional work and content quality.</p>
          </div>
        )}

        <div className="flex flex-col gap-4">
          {ratings.map(rating => {
            const date = formatDate(rating.updated_at || rating.created_at)

            return (
              <div key={rating.id} className={`bg-white dark:bg-rb-dark-mode p-5 rounded-xl border-1 ${isOwn(rating) ? 'border-rb-green/40' : 'border-transparent'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {rating?.project_manager?.avatar
                      ? <img className="h-11 w-11 rounded-full object-cover" src={rating.project_manager.avatar} />
                      : <div className="h-11 w-11 rounded-full bg-gray-100 dark:bg-rb-dark-mode-lighter flex items-center justify-center">
                          <Person className="text-gray-500 dark:text-white" />
                        </div>
                    }
                    <div className="ml-3">
                      <div className="flex items-center gap-2">
                        <p className="font-bold dark:text-white">{rating?.project_manager?.first_name} {rating?.project_manager?.last_name}</p>
                        {isOwn(rating) && <span className="bg-rb-green/15 text-rb-green text-xs font-bold px-2 py-0.5 rounded-full">You</span>}
                      </div>
                      <p className="text-xs text-gray-400">{rating?.project_manager?.rating_count} review{rating?.project_manager?.rating_count === 1 ? '' : 's'} written</p>
                    </div>
                  </div>
                  {date && <p className="text-xs text-gray-400">{date}</p>}
                </div>

                <div className="flex gap-6 mt-4 pt-4 border-t-1 border-gray-100 dark:border-rb-dark-mode-lighter">
                  <ScoreRow title="Professional work" score={rating.professional_score} description={rating.professional_description} />
                  <div className="w-px bg-gray-100 dark:bg-rb-dark-mode-lighter" />
                  <ScoreRow title="Quality of content" score={rating.quality_score} description={rating.quality_description} />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
})
